"use client";

import * as React from "react";
import type { DateRange } from "react-day-picker";
import { getUserInstances, type UserInstance } from "@/api/instances";

type AppFilters = {
  // existing stuff
  instance: string; // instance_id
  dateRange: DateRange | undefined;
  setInstance: (value: string) => void;
  setDateRange: (value: DateRange | undefined) => void;

  // new stuff
  instances: UserInstance[];
  currentInstance: UserInstance | null;
};

const AppFiltersContext = React.createContext<AppFilters | null>(null);

export function AppFiltersProvider({ children }: { children: React.ReactNode }) {
  const [instance, setInstance] = React.useState(""); // selected instance_id
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    undefined
  );

  const [instances, setInstances] = React.useState<UserInstance[]>([]);

  // Load instances once
  React.useEffect(() => {
    const loadInstances = async () => {
      try {
        const data = await getUserInstances(); // UserInstance[] | null
        if (!data) return;

        setInstances(data);

        // If nothing selected yet, default to first instance
        if (!instance && data.length > 0) {
          setInstance(data[0].instance_id);
        }
      } catch (e) {
        console.error("Failed to load instances in AppFiltersProvider:", e);
      }
    };

    loadInstances();
  }, [instance]);

  // Derive the full selected instance object
  const currentInstance = React.useMemo(
    () =>
      instances.find((inst) => inst.instance_id === instance) ?? null,
    [instances, instance]
  );

  const value = React.useMemo(
    () => ({
      instance,
      dateRange,
      setInstance,
      setDateRange,
      instances,
      currentInstance,
    }),
    [instance, dateRange, instances, currentInstance]
  );

  return (
    <AppFiltersContext.Provider value={value}>
      {children}
    </AppFiltersContext.Provider>
  );
}

export function useAppFilters(): AppFilters {
  const ctx = React.useContext(AppFiltersContext);
  if (!ctx) {
    throw new Error("useAppFilters must be used inside <AppFiltersProvider>");
  }
  return ctx;
}
